import { ASK_AN_EXPERT, FORUM } from 'mk/autogenerated/translations/structuredData.c02ede25e8f86ab9300b15ddcf7e1e9e'
import { photoUrl } from 'mk/photo/photoUrl';
import { counsellingsHomeUrl, counsellingsPostDetailUrl, counsellingsTopicUrl } from 'mk2/apps/counsellings/urls';
import { CategoryEntity, PostEntity, TopicEntity } from 'mk2/apps/forum/schemas';
import { forumPostDetailUrl, forumTopicsCategoryUrl, forumTopicUrl, forumUrl } from 'mk2/apps/forum/urls';
import { isCounsellingPost, largestAvailablePhotoVersionOfPost } from 'mk2/apps/forum/utils';
import { mainImageUrlWithFallback } from 'mk2/helpers/structuredData';
import { YMDHMSIso } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import { PhotoEntity } from 'mk2/schemas';

const breadcrumbCounsellings = (baseUrl: string) => ({
    '@type': 'ListItem',
    'position': 1,
    'item': {
        '@id': baseUrl + counsellingsHomeUrl,
        'name': ASK_AN_EXPERT,
    },
});

const breadcrumbForum = (baseUrl: string) => ({
    '@type': 'ListItem',
    'position': 1,
    'item': {
        '@id': baseUrl + forumUrl,
        'name': FORUM,
    },
});

const breadcrumbSubforum = (baseUrl: string, subforum: string, subforumUrl: string) => ({
    '@type': 'ListItem',
    'position': 2,
    'item': {
        '@id': baseUrl + subforumUrl,
        'name': subforum,
    },
});

const breadcrumbTopic = (baseUrl: string, topic: TopicEntity, route = forumTopicUrl) => ({
    '@type': 'ListItem',
    'position': 2,
    'item': {
        '@id': baseUrl + url(route, {topicSlug: topic.slug}),
        'name': topic.name,
    },
});

const breadcrumbCategory = (baseUrl: string, category: CategoryEntity) => ({
    '@type': 'ListItem',
    'position': 2,
    'item': {
        '@id': baseUrl + url(forumTopicsCategoryUrl, {categorySlug: category.slug}),
        'name': category.name,
    },
});

const breadcrumbPost = (baseUrl: string, topic: TopicEntity, post: PostEntity, route = forumPostDetailUrl) => ({
    '@type': 'ListItem',
    'position': 3,
    'item': {
        '@id': baseUrl + url(route, {topicSlug: topic.slug, postSlug: post.slug}),
        'name': post.heading,
    },
});

export function breadcrumbsCounsellings(baseUrl, topic?: TopicEntity, post?: PostEntity) {
    const items = [ breadcrumbCounsellings(baseUrl) ];

    if (topic) {
        items.push(breadcrumbTopic(baseUrl, topic, counsellingsTopicUrl));
    }

    if (post) {
        items.push(breadcrumbPost(baseUrl, topic, post, counsellingsPostDetailUrl));
    }

    return {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
    };
}

export function breadcrumbsForum(baseUrl, category?: CategoryEntity, topic?: TopicEntity, post?: PostEntity) {
    const items = [ breadcrumbForum(baseUrl) ];

    if (category) {
        items.push(breadcrumbCategory(baseUrl, category));
    }

    if (topic) {
        items.push(breadcrumbTopic(baseUrl, topic));
    }

    if (post) {
        items.push(breadcrumbPost(baseUrl, topic, post));
    }

    return {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
    };
}

export function breadcrumbsSubforum(baseUrl: string, subForum: string, subforumUrl: string) {
    const items = [ breadcrumbForum(baseUrl) ];

    items.push(breadcrumbSubforum(baseUrl, subForum, subforumUrl));

    return {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
    };
}

export function discussionForumPosting(baseUrl: string, post: PostEntity) {
    const createdOn: string = YMDHMSIso(post.created);
    const modifiedOn: string = YMDHMSIso(post.lastMessageTime);
    const { photo: mainPhoto, version: mainPhotoVersion } = mainPhotoOfPost(post);

    // fields according to https://developers.google.com/search/docs/data-types/article (non-AMP pages)
    return {
        '@context': 'http://schema.org',
        '@type': 'DiscussionForumPosting',
        '@id': baseUrl + url(forumPostDetailUrl, {topicSlug: post.topic.slug, postSlug: post.slug}),
        'headline': post.heading,
        'image': mainPhoto ? photoUrl(mainPhoto, mainPhotoVersion) : undefined,
        'datePublished': createdOn,
        'dateModified': createdOn !== modifiedOn ? modifiedOn : undefined,
        'author': {
            '@type': 'Person',
            'name': post.author.username,
        },
        'interactionStatistic': {
            '@type': 'InteractionCounter',
            'interactionType': 'http://schema.org/CommentAction',
            'userInteractionCount': post.comments,
        },
    };
}

function mainPhotoOfPost(post: PostEntity): {photo: PhotoEntity, version: string} {
    if (post.photos.length > 0) {
        return {
            photo: post.photos[0],
            version: largestAvailablePhotoVersionOfPost(post.photos[0], 's1600x1600'),
        };
    } else if (isCounsellingPost(post) && post.topic.coverPhoto) {
        return {
            photo: post.topic.coverPhoto,
            version: 's1600x1600',
        };
    } else {
        return {
            photo: null,
            version: null,
        };
    }
}

export function topicOG(baseUrl: string, topic: TopicEntity) {
    return {
        'og:image': mainImageUrlWithFallback(topic.coverPhoto),
    };
}

export function postOG(baseUrl: string, post: PostEntity) {
    const { photo: mainPhoto, version: mainPhotoVersion } = mainPhotoOfPost(post);

    return {
        'og:image': mainPhoto
            ? photoUrl(mainPhoto, mainPhotoVersion)
            : mainImageUrlWithFallback(mainPhoto),
    };
}
